// The on-demand course (Property Cashflow For Life), worked out from the product's own rows
// (src/product/data.js) the way PCFL v3 works it out (reference/pcfl-v3/source/app.jsx, buildModel),
// for a member with no intake. Pure: no network and no React, so a test can import it.
//
// - Every recording that's switched on is open (row level security returns its content once it's
//   released; on an on-demand course that's as soon as it's switched on). One that isn't is "Coming
//   soon".
// - Where they're up to is the first module, the Introduction included, that isn't finished: every
//   open recording in it watched and its homework all ticked.
// - A side of the pentagon lights (solid) once that module's homework is all ticked; the module
//   they're up to is drawn dashed.
// - What to watch next: a recording they stopped part way through (more than 10 seconds in) first,
//   else the first open one they haven't watched.
// - Start here's steps are done when ticked (step_progress.done_at), or, for a recording step, when
//   the recording is watched, and for a homework step, when its homework is all ticked.
// - Pick your patch's episodes are the steps in the 'episodes' section, each with its own place.
//
// Homework counts kind 'homework' only, as everywhere in the hub; PCFL has no commitments.
import * as f from '../../lib/format.js'
import { isReleased, myIntake, progressFor } from '../../product/model.js'

const STARTED = 10 // seconds in before a recording counts as started (Carry on from)

const byOrder = (a, b) => (a.sort_order - b.sort_order) || String(a.id).localeCompare(String(b.id))

// The modules in order: the Introduction (no number), then Module 1 to Module 5
export function modulesOf(data) {
  return (data?.modules || []).filter(m => m.is_published).sort(byOrder)
}

// The numbered modules, which are the pentagon's sides
export function numberedOf(data) {
  return modulesOf(data).filter(m => m.number != null)
}

const titleOf = m => (m?.title && !f.isPlaceholder(m.title) ? m.title : m?.label || '')

// "Module 3, Money", or "Introduction" for a module with no number; a module whose title is still to
// come (a new course's "[Module 3 title]") is "Module 3"
export function moduleLine(m) {
  if (!m) return ''
  return m.number == null || titleOf(m) === m.label ? m.label : `${m.label}, ${titleOf(m)}`
}

// "Module 3, Money, Part A: Stack it", "Module 1, Motivation", "Introduction"
export function recordingName(rec, m) {
  if (!m) return rec?.title || ''
  return moduleLine(m) + (rec?.part ? `, Part ${rec.part}: ${rec.title}` : '')
}

// What the thumbnail says in its corner: "Module 3, Part A", "Introduction"
export function thumbLabel(rec, m) {
  if (!m) return rec?.title || ''
  if (m.number == null) return m.label
  return m.label + (rec?.part ? `, Part ${rec.part}` : '')
}

// The page a recording opens on: the module's own path where it has 1 recording (/cashflow/m1), the
// recording's where it has parts (/cashflow/m3a)
export function recordingHref(product, data, rec) {
  const base = product?.path || ''
  const m = (data?.modules || []).find(x => x.id === rec.module_id)
  const siblings = (data?.recordings || []).filter(r => r.module_id === rec.module_id)
  if (m && siblings.length === 1) return `${base}/${m.key}`
  return `${base}/${rec.key || m?.key || ''}`
}

// Every recording, in order, with where they are in it
export function recordingRows(data) {
  const mods = modulesOf(data)
  const content = new Map((data?.content || []).map(c => [c.recording_id, c]))
  return (data?.recordings || []).slice().sort(byOrder).map(r => {
    const module = mods.find(m => m.id === r.module_id) || null
    const c = content.get(r.id)
    const pr = progressFor(data, r.id)
    const ready = isReleased(data, r.id)
    return {
      ...r,
      module,
      ready,
      duration: c?.duration_seconds || 0,
      chapters: c?.chapters || [],
      watched: Boolean(pr?.watched_at),
      pos: pr?.position_seconds || 0,
      furthest: pr?.furthest_seconds || 0,
    }
  })
}

// A recording's status line, as PCFL v3's chips have it
export function recordingStatus(row) {
  if (!row.ready) return { tone: 'soon', text: 'Coming soon' }
  if (row.watched) return { tone: 'done', text: 'Watched' }
  if (row.pos > STARTED) return { tone: 'todo', text: `Carry on from ${f.clock(row.pos)}` }
  return { tone: 'todo', text: 'Not watched yet' }
}

export const lessons = n => `${n} ${n === 1 ? 'lesson' : 'lessons'}`

// ---- Homework

// A module's homework, in order (the screens show every kind; only kind 'homework' counts)
export function homeworkOf(data, moduleId) {
  return (data?.homework || []).filter(h => h.module_id === moduleId).sort(byOrder)
}
const counted = (data, moduleId) => homeworkOf(data, moduleId).filter(h => h.kind !== 'commitment')

export function homeworkCount(data, moduleId) {
  const items = counted(data, moduleId)
  const ticks = data?.ticks || []
  return { done: items.filter(h => ticks.includes(h.id)).length, total: items.length }
}

export function homeworkDone(data, moduleId) {
  const { done, total } = homeworkCount(data, moduleId)
  return total > 0 && done >= total
}

// "Homework before Module 2", or "Your last homework" for the last module
export function homeworkTitle(data, m) {
  const mods = modulesOf(data)
  const i = mods.findIndex(x => x.id === m?.id)
  const next = mods.slice(i + 1).find(x => x.number != null)
  return next ? `Homework before ${next.label}` : 'Your last homework'
}

// "Module 1 homework", "Introduction homework"
export const homeworkName = m => `${m.label} homework`

// The homework's groups as PCFL v3 draws them: a heading ("The tasks" where there's none) and the
// time it takes, then the items
export function homeworkGroups(items) {
  const groups = []
  for (const h of items) {
    const heading = h.group_heading || null
    const g = groups[groups.length - 1]
    if (g && g.heading === heading) g.items.push(h)
    else groups.push({ heading, time: h.time_label || null, items: [h] })
  }
  return groups.map(g => ({ ...g, title: g.heading || 'The tasks' }))
}

// ---- Steps: Start here and Pick your patch

export function stepsOf(data, section) {
  return (data?.steps || []).filter(s => s.section === section && s.is_published !== false).sort(byOrder)
}

export function stepRow(data, stepId) {
  return (data?.stepProgress || []).find(p => p.step_id === stepId) || null
}

// The words of a step: the on-demand version where it has one and they have no intake
export function stepBody(data, step) {
  const onDemand = !myIntake(data)
  const od = step?.config?.body_ondemand
  return onDemand && typeof od === 'string' && od.trim() ? od : step?.body_md || ''
}

// Where a link step goes: Pick your patch for v3's 'patch', else the product's page of that name
export function stepTarget(product, step) {
  const t = step?.config?.target
  if (!t || typeof t !== 'string' || !/^[a-z0-9-]+$/.test(t)) return null
  return `${product?.path || ''}/${t === 'patch' ? 'episodes' : t}`
}

// The episodes of Pick your patch, numbered, with where they are in each
export function episodesOf(data) {
  return stepsOf(data, 'episodes').map((s, i) => {
    const p = stepRow(data, s.id)
    const url = typeof s.config?.video_url === 'string' ? s.config.video_url : null
    return {
      id: s.id, key: s.step_key, n: i + 1, title: s.title, line: typeof s.config?.line === 'string' ? s.config.line : '',
      videoUrl: url, watched: Boolean(p?.done_at), pos: p?.position_seconds || 0,
    }
  })
}

// ---- Where they're up to

export function courseModel(data, product) {
  const mods = modulesOf(data)
  const numbered = mods.filter(m => m.number != null)
  const recs = recordingRows(data)
  const recsOf = moduleId => recs.filter(r => r.module_id === moduleId)
  const hwDone = id => homeworkDone(data, id)
  // a module with nothing in it yet (a new course's, before Adam adds its recording or homework: the
  // product-types review, 13 Oct) isn't done; every module of Property Cashflow For Life has both
  const finished = m => {
    const hw = counted(data, m.id)
    if (!recsOf(m.id).length && !hw.length) return false
    return recsOf(m.id).every(r => !r.ready || r.watched) && (hw.length === 0 || hwDone(m.id))
  }

  const position = mods.find(m => !finished(m)) || null
  const states = numbered.map(m => (hwDone(m.id) ? 'done' : m.id === position?.id ? 'current' : 'todo'))
  const openHw = position && counted(data, position.id).length && !hwDone(position.id) ? position : null

  const ready = recs.filter(r => r.ready)
  const carry = ready.find(r => !r.watched && r.pos > STARTED) || null
  const watchNext = carry || ready.find(r => !r.watched) || null

  // Start here
  const recById = new Map(recs.map(r => [r.id, r]))
  const startSteps = stepsOf(data, 'start_here')
  const stepDone = s => Boolean(stepRow(data, s.id)?.done_at)
    || (s.step_type === 'recording' && Boolean(recById.get(s.recording_id)?.watched))
    || (s.step_type === 'homework' && Boolean(s.module_id) && hwDone(s.module_id))
  const startDone = startSteps.filter(stepDone).length

  // the words on the where card
  let headline, line
  if (!position) {
    // the 5 Ms are Property Cashflow For Life's own (as the pentagon below)
    headline = numbered.length === 5 && product?.slug === 'cashflow' ? 'All 5 Ms done' : 'Every module done'
    line = 'Every recording and resource stays here for your next deal.'
  } else if (position.number == null) {
    headline = 'Start here'
    line = `The ${position.label} and its homework come first.`
  } else {
    headline = moduleLine(position)
    line = position.teaser && !f.isPlaceholder(position.teaser) ? position.teaser : ''
  }
  let status
  if (openHw) {
    const c = homeworkCount(data, openHw.id)
    status = { tone: 'open', text: `${homeworkName(openHw)}: ${c.done} of ${c.total} done.` }
  } else {
    status = { tone: 'ok', text: 'Your homework is up to date.' }
  }

  const firstNumbered = numbered[0] || null
  return {
    modules: mods, numbered, recs, recsOf, hwDone, finished, position, states, openHw, carry, watchNext,
    startSteps, stepDone, startDone, startTotal: startSteps.length, firstNumbered,
    where: { headline, line, status },
    certificate: data?.certificate || null,
    // the 5 Ms are Property Cashflow For Life's own, so another on-demand course of 5 modules (the
    // product-types plan, 13 Oct) shows how many are done instead
    pentagon: numbered.length === 5 && product?.slug === 'cashflow',
    path: product?.path || '',
  }
}

// The watch card on Home: what to watch next, with the bar when they're part way through
export function watchCard(model, product, data) {
  const w = model.watchNext
  if (!w) return null
  const carry = model.carry && model.carry.id === w.id
  return {
    kicker: carry ? 'Carry on watching' : 'Watch next',
    title: recordingName(w, w.module),
    line: carry ? `Carry on from ${f.clock(w.pos)}` : (w.duration ? f.duration(w.duration) : 'Ready to watch'),
    pct: carry && w.duration ? Math.min(100, Math.round((w.pos / w.duration) * 100)) : null,
    button: { label: carry ? 'Carry on' : 'Watch', href: recordingHref(product, data, w) },
    rec: w,
  }
}

// The Start here card on Home, until every step is done
export function startCard(model, product) {
  if (!model.startTotal || model.startDone >= model.startTotal) return null
  return {
    kicker: 'Start here',
    title: `${model.startDone} of ${model.startTotal} done`,
    line: model.firstNumbered ? `Do these before ${model.firstNumbered.label}.` : 'Do these first.',
    button: { label: 'Carry on with Start here', href: `${product?.path || ''}/start-here` },
  }
}

// Search across the open recordings' titles, the modules' lines and the chapters (PCFL v3's
// Recordings): the recording when its name matches, and each chapter that does
export function searchRows(rows, term) {
  const q = String(term || '').trim().toLowerCase()
  if (!q) return rows.map(r => ({ row: r, hits: [] }))
  const out = []
  for (const r of rows) {
    const words = [r.title, r.module?.title, r.module?.label, r.module?.teaser].filter(Boolean).join(' ').toLowerCase()
    const hits = r.chapters.filter(c => `${c.no ?? ''} ${c.title}`.toLowerCase().includes(q))
    if (words.includes(q) || hits.length) out.push({ row: r, hits })
  }
  return out
}

// "1.2 The seller situations": a chapter with its lesson number
export function chapterName(c) {
  const no = c?.no == null || c.no === '' ? '' : `${c.no} `
  return `${no}${c?.title || ''}`
}

// PCFL v3's mdLite, as data the screen draws as elements (never raw HTML): blocks split by a blank
// line; a block whose every line starts "- " is a list, any other block a paragraph
export function mdBlocks(src) {
  return String(src || '').trim().split(/\n\s*\n/).filter(b => b.trim()).map(b => {
    const lines = b.split('\n').map(l => l.trim()).filter(Boolean)
    if (lines.length && lines.every(l => /^- /.test(l))) return { list: lines.map(l => l.slice(2)) }
    return { text: lines.join(' ') }
  })
}
